/**
 * Antd v5 static-API bridge.
 *
 * Under React 19, antd's *static* imperative helpers (message.error,
 * Modal.confirm, notification.info) render outside the current React root
 * via a private createRoot call. Without an <App> ancestor in the real
 * tree, that render often does nothing visible — hence the class of
 * "click, nothing happens" bugs we've been chasing (silent 409s from the
 * server that never surfaced as a toast).
 *
 * The fix: wrap the tree in antd's <App> (see main.tsx), then use
 * App.useApp() from a child to grab context-scoped message / notification /
 * modal instances that DO render reliably. This bridge stashes those
 * instances in module-level slots so non-React code (the API client,
 * the errors helper) can reach them.
 */
import { useEffect } from 'react';
import { App as AntApp } from 'antd';
let messageApi = null;
let notificationApi = null;
let modalApi = null;
/**
 * Renders nothing; captures the antd hooks once mounted. Must be a child
 * of antd's <App> component.
 */
export function AntdBridge() {
    const { message, notification, modal } = AntApp.useApp();
    // Capturing on mount means the first render after <App> is in place has
    // the instances ready. useEffect guarantees consumers only see them
    // after commit so pre-mount log lines can be sensibly console-fallback'd.
    useEffect(() => {
        messageApi = message;
        notificationApi = notification;
        modalApi = modal;
    }, [message, notification, modal]);
    return null;
}
export function getMessage() {
    return messageApi;
}
export function getNotification() {
    return notificationApi;
}
export function getModal() {
    return modalApi;
}
